import React, { useState, useRef, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { TENANT_OPTIONS, getActiveTenantId, setActiveTenantId, getMyTenants } from '../../services/tenantContext';

/**
 * Company data-source switcher. Every cost-api request already carries
 * an x-tenant-id header (tenantContextMiddleware scopes every query by it) -
 * this just lets the UI pick which tenant ID to send, instead of every page
 * hardcoding the same one. No backend changes are needed for the switch
 * itself: the backend already returns whatever data exists for a given
 * tenant_id.
 *
 * Switching reloads the app so every page refetches under the new tenant
 * scope. "Apexon (Real Data)" will show an honestly empty dashboard until
 * real cloud accounts are onboarded under that tenant - a separate, already
 * tracked gap blocked on cloud provider access, not something this switch
 * can fix by itself.
 */
export const TenantSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTenantId = getActiveTenantId();

  /**
   * The tenants this user may actually switch to.
   *
   * Previously the hardcoded TENANT_OPTIONS list was shown to everyone, and
   * switching was pure client state - so the menu offered every tenant to
   * every user regardless of entitlement. The server now authorizes each
   * request, so the menu must stop offering doors that are locked, otherwise
   * picking one just produces a wall of errors that reads as a broken app
   * rather than a permission boundary.
   *
   * Starts as the full list and narrows once the call returns. Erring towards
   * showing MORE is deliberate: the server is the thing that enforces, and a
   * switcher that renders empty for a moment would be worse than one that
   * briefly over-offers.
   */
  const [allowedTenants, setAllowedTenants] = useState(TENANT_OPTIONS);

  useEffect(() => {
    let cancelled = false;
    getMyTenants()
      .then((tenants) => {
        if (cancelled || tenants.length === 0) return;
        setAllowedTenants(tenants);
      })
      .catch(() => {
        // Keep the full list. The server still enforces on every request, so
        // failing to narrow the menu is a cosmetic problem, not a hole.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeTenant =
    allowedTenants.find((t) => t.id === activeTenantId) ||
    TENANT_OPTIONS.find((t) => t.id === activeTenantId) ||
    allowedTenants[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (tenantId: string) => {
    setIsOpen(false);
    if (tenantId === activeTenantId) return;

    const target = allowedTenants.find((t) => t.id === tenantId);
    if (!target) return;

    if (confirm(`Switch to "${target.label}"?\n\nThe app will reload and show data for this company.`)) {
      setActiveTenantId(tenantId);
      window.location.reload();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Same metrics as the shared FilterSelect trigger used on the Dashboard
          - h-8, px-2.5, text-xs, a 3px chevron. This was h-auto with px-3 py-2
          text-sm and a 4px chevron, which made it visibly taller and heavier
          than every other control in the app. */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Company: ${activeTenant.label}`}
        className="flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border bg-card px-2.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate">{activeTenant.label}</span>
        <svg
          className={`h-3 w-3 shrink-0 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Switch company"
          className="absolute right-0 z-50 mt-1 w-60 overflow-hidden rounded-md border bg-popover p-1 shadow-md"
        >
          {allowedTenants.map((option) => {
            const isSelected = option.id === activeTenantId;
            return (
              <li
                key={option.id}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option.id)}
                className={`cursor-pointer rounded-sm px-2 py-1.5 text-xs transition-colors ${
                  isSelected
                    ? 'bg-primary/10 text-primary dark:bg-primary/10 dark:text-primary'
                    : 'text-foreground/80 hover:bg-secondary dark:text-foreground dark:hover:bg-secondary'
                }`}
              >
                <div className="font-medium">{option.label}</div>
                <div className="mt-0.5 text-2xs text-muted-foreground">{option.description}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
